import assert from 'assert';
import { computed, type Ref } from 'vue';
import { ScanUploadState } from '@/views/case-settings/upload/shared/ScanUploadState';
import { PatientComparisonUtil } from '@/lib/dicom/PatientComparisonUtil';
import type { PatientPersonalInfo } from '@/lib/dicom/types';
import type { UploadState } from '@/views/case-settings/webworkers/types';

/**
 * The slice of the upload context the derived state reads — just the reactive
 * UploadState. Narrower than the full DicomUploadContext (interface segregation)
 * so these helpers carry no generics and stay trivially unit-testable; the full
 * context is assignable to it.
 */
type UploadStateView = { state: Ref<UploadState> };

/**
 * Patient personal info derived from the current patient-identity sample file.
 * Shared by the `patient` flag and the validation action (which feeds it to
 * `comparePatient`), so the derivation lives in one place.
 */
export function dicomPatientInfo(ctx: UploadStateView): PatientPersonalInfo {
    assert.ok(ctx.state.value.patientIdentityCheck, 'patientIdentityCheck is not defined');
    return PatientComparisonUtil.fromDicom(ctx.state.value.patientIdentityCheck.sampleFile);
}

/**
 * Derived state (the `is*` predicates + `patient`) for the upload lifecycle,
 * built over the reactive UploadState. Extracted from the composable body so the
 * setup function stays within the function-length cap; behaviour is identical to
 * the inline computeds it replaces.
 */
export function createDicomUploadFlags(ctx: UploadStateView) {
    const { state } = ctx;
    return {
        hasMultiplePatientIdentities: computed(
            () => state.value.state === ScanUploadState.PatientInconsistentIdentityError,
        ),
        isPatientDicomMismatch: computed(
            () => state.value.state === ScanUploadState.EnteredPatientVsDicomDataMismatch,
        ),
        isUploading: computed(() => state.value.state === ScanUploadState.UploadingData),
        isWaitingForConfirmation: computed(
            () => state.value.state === ScanUploadState.WaitingForUploadConfirmation,
        ),
        isWaitingForSeriesSelection: computed(
            () => state.value.state === ScanUploadState.WaitingForSeriesSelection,
        ),
        isCollecting: computed(() => state.value.state === ScanUploadState.CollectingData),
        isError: computed(() => state.value.state === ScanUploadState.Error),
        isModalError: computed(() => state.value.state === ScanUploadState.InModalError),
        patient: computed((): PatientPersonalInfo => dicomPatientInfo(ctx)),
    };
}
